import { useState } from 'react';
import InputField from '../../../../components/InputField';
import SelectTwo from '../../../../components/SelectTwo';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import LazyLoading from '../../../../components/LazyLoading';

interface ChatDocument {
    id: number;
    name: string;
    description: string;
    file_type: string;
    file_size: number;
    file_size_formatted: string;
    file_url: string;
    category: string;
    category_display: string;
    sync_status: string;
    sync_status_display: string;
    sync_error: string | null;
    synced_at: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

interface Props {
    documents: ChatDocument[];
    totalRows: number;
    loading: boolean;
    filter: any;
    setFilter: (filter: any) => void;
    edit: (id: number) => void;
    destroy: (id: number) => void;
    active: (id: number) => void;
    sync: (id: number) => void;
    callbackSuccess: () => void;
    onSearch: () => void;
}

const categoryOptions = [
    { value: '', label: 'Todas las categorías' },
    { value: 'manual', label: 'Manual de Usuario' },
    { value: 'policy', label: 'Política Interna' },
    { value: 'procedure', label: 'Procedimiento' },
    { value: 'contract', label: 'Contrato' },
    { value: 'faq', label: 'Preguntas Frecuentes' },
    { value: 'legal', label: 'Documento Legal' },
    { value: 'other', label: 'Otro' }
];

const syncStatusOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'uploading', label: 'Subiendo' },
    { value: 'processing', label: 'Procesando' },
    { value: 'synced', label: 'Sincronizado' },
    { value: 'error', label: 'Error' }
];

const activeOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Activos' },
    { value: 'false', label: 'Inactivos' }
];

const getSyncStatusBadge = (status: string, statusDisplay: string) => {
    const colors: Record<string, string> = {
        'pending': 'bg-secondary',
        'uploading': 'bg-info',
        'processing': 'bg-warning',
        'synced': 'bg-success',
        'error': 'bg-danger'
    };
    return (
        <span className={`badge ${colors[status] || 'bg-secondary'}`}>
            {statusDisplay}
        </span>
    );
};

const getFileTypeIcon = (fileType: string) => {
    const icons: Record<string, string> = {
        'pdf': 'fa-file-pdf text-danger',
        'doc': 'fa-file-word text-primary',
        'docx': 'fa-file-word text-primary',
        'txt': 'fa-file-alt text-muted'
    };
    return `fas ${icons[fileType] || 'fa-file text-muted'}`;
};

const ChatDocumentDataTable = ({
    documents,
    totalRows,
    loading,
    filter,
    setFilter,
    edit,
    destroy,
    active,
    sync,
    callbackSuccess,
    onSearch
}: Props) => {
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

    const handleSearchChange = (value: string) => {
        setFilter({ ...filter, search: value });
        
        if (searchTimeout) clearTimeout(searchTimeout);
        const timeout = setTimeout(() => {
            onSearch();
        }, 500);
        setSearchTimeout(timeout);
    };

    return (
        <div>
            {/* Filtros */}
            <div className="row mb-4">
                <div className="col-md-4">
                    <InputField
                        label=""
                        value={filter.search}
                        onChange={handleSearchChange}
                        placeholder="Buscar por nombre..."
                        icon="fa-search"
                    />
                </div>
                <div className="col-md-3">
                    <SelectTwo
                        label=""
                        name="category"
                        inputValue={filter.category}
                        options={categoryOptions}
                        onChange={(option: any) => setFilter({ ...filter, category: option?.value || '' })}
                        placeholder="Filtrar por categoría"
                    />
                </div>
                <div className="col-md-3">
                    <SelectTwo
                        label=""
                        name="sync_status"
                        inputValue={filter.sync_status}
                        options={syncStatusOptions}
                        onChange={(option: any) => setFilter({ ...filter, sync_status: option?.value || '' })}
                        placeholder="Estado de sincronización"
                    />
                </div>
                <div className="col-md-2">
                    <SelectTwo
                        label=""
                        name="is_active"
                        inputValue={filter.is_active}
                        options={activeOptions}
                        onChange={(option: any) => setFilter({ ...filter, is_active: option?.value || '' })}
                        placeholder="Estado"
                    />
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <LazyLoading height={300} />
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th style={{ width: '30%' }}>Documento</th>
                                    <th style={{ width: '15%' }}>Categoría</th>
                                    <th style={{ width: '10%' }}>Tamaño</th>
                                    <th style={{ width: '15%' }}>Sincronización</th>
                                    <th style={{ width: '10%' }}>Estado</th>
                                    <th style={{ width: '20%' }} className="text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-5 text-muted">
                                            <i className="fas fa-folder-open fa-3x mb-3 d-block"></i>
                                            No hay documentos registrados
                                        </td>
                                    </tr>
                                ) : (
                                    documents.map((doc) => (
                                        <tr key={doc.id}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <i className={`${getFileTypeIcon(doc.file_type)} fa-lg me-3`}></i>
                                                    <div>
                                                        <strong>{doc.name}</strong>
                                                        {doc.description && (
                                                            <p className="mb-0 text-muted small text-truncate" style={{ maxWidth: '250px' }}>
                                                                {doc.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="badge bg-light text-dark">
                                                    {doc.category_display}
                                                </span>
                                            </td>
                                            <td>{doc.file_size_formatted}</td>
                                            <td>
                                                {getSyncStatusBadge(doc.sync_status, doc.sync_status_display)}
                                                {doc.sync_error && (
                                                    <i 
                                                        className="fas fa-exclamation-circle text-danger ms-2" 
                                                        title={doc.sync_error}
                                                        style={{ cursor: 'help' }}
                                                    ></i>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${doc.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                    {doc.is_active ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center justify-content-center gap-1">
                                                    {doc.sync_status === 'error' && (
                                                        <ButtonTableAction
                                                            callbackFunction={() => sync(doc.id)}
                                                            title="Re-sincronizar"
                                                            classIcon="fa-sync"
                                                            colorIcon="text-info"
                                                            sizeIcon="16"
                                                        />
                                                    )}
                                                    <ButtonTableAction
                                                        callbackFunction={() => edit(doc.id)}
                                                        title="Editar"
                                                        classIcon="fa-edit"
                                                        colorIcon="text-warning"
                                                        sizeIcon="16"
                                                    />
                                                    <ButtonTableAction
                                                        callbackFunction={() => active(doc.id)}
                                                        title={doc.is_active ? 'Desactivar' : 'Activar'}
                                                        classIcon={doc.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}
                                                        colorIcon={doc.is_active ? 'text-success' : 'text-secondary'}
                                                        sizeIcon="16"
                                                    />
                                                    <ButtonTableAction
                                                        callbackFunction={() => destroy(doc.id)}
                                                        title="Eliminar"
                                                        classIcon="fa-trash"
                                                        colorIcon="text-danger"
                                                        sizeIcon="16"
                                                    />
                                                    {doc.file_url && (
                                                        <a 
                                                            href={doc.file_url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-link p-1"
                                                            title="Descargar"
                                                        >
                                                            <i className="fas fa-download text-primary" style={{ fontSize: '16px' }}></i>
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Contador */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                            Mostrando {documents.length} de {totalRows} documentos
                        </small>
                    </div>
                </>
            )}
        </div>
    );
};

export default ChatDocumentDataTable;

